import { useCallback } from "react";

import type {
  ClaimedCard,
  ClaimedCardAction,
  ClaimEligibilityCta,
  ClaimReasonData,
  ClaimReasonNode,
  ViewDetailData,
} from "../types";
import { useClaimModal } from "./ModalContext";
import { ModalActionId } from "./types";

const OPEN_URL = "OPEN_URL";

export interface ClaimActionExtra {
  viewDetailData?: ViewDetailData;
  reasonData?: ClaimReasonData;
  onSelectNode?: (node: ClaimReasonNode) => void;
  eligibilityData?: NonNullable<ClaimEligibilityCta["action"]>["data"];
}

export interface ClaimActionInput {
  action?: ClaimedCardAction;
  card?: ClaimedCard;
  extra?: ClaimActionExtra;
}

function isWebView(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as {
    mmt_android_bridge?: unknown;
    app_bridge?: unknown;
    webkit?: { messageHandlers?: Record<string, unknown> };
  };
  if (w.app_bridge || w.mmt_android_bridge) return true;
  const handlers = w.webkit?.messageHandlers;
  return !!handlers && Object.keys(handlers).length > 0;
}

function openClaimUrl(url: string) {
  if (typeof window === "undefined" || !url) return;
  if (isWebView()) {
    window.location.assign(url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

export function useClaimAction() {
  const { openModal } = useClaimModal();

  return useCallback(
    (actionId: string | undefined, input: ClaimActionInput = {}) => {
      if (!actionId) return;
      const { action, card, extra } = input;

      switch (actionId) {
        case ModalActionId.VIEW_TRAVELLER_DETAIL: {
          const data = extra?.viewDetailData ?? action?.viewDetailData;
          if (data) openModal(ModalActionId.VIEW_TRAVELLER_DETAIL, data);
          return;
        }
        case ModalActionId.OPEN_CLAIM_REASON: {
          const { reasonData, onSelectNode } = extra ?? {};
          if (reasonData && onSelectNode) {
            openModal(ModalActionId.OPEN_CLAIM_REASON, {
              reasonData,
              onSelectNode,
            });
          }
          return;
        }
        case ModalActionId.CHECK_ELIGIBILITY: {
          const data = extra?.eligibilityData;
          if (data?.criteria?.length) {
            openModal(ModalActionId.CHECK_ELIGIBILITY, { data });
          }
          return;
        }
        case ModalActionId.OPEN_CLAIM_DETAIL_BOTTOM_SHEET: {
          if (card?.detailSheet) {
            openModal(
              ModalActionId.OPEN_CLAIM_DETAIL_BOTTOM_SHEET,
              card.detailSheet
            );
          }
          return;
        }
        case OPEN_URL: {
          if (action?.url) openClaimUrl(action.url);
          return;
        }
        default: {
          if (action?.url) openClaimUrl(action.url);
        }
      }
    },
    [openModal]
  );
}
